import { useWeb3React } from "@web3-react/core";
import { ethers } from "ethers";
import type { NextPage } from "next";
import { useEffect, useMemo, useReducer, useState } from "react";
import { useMedia } from "react-use";
import { shuffleArray } from "../../services/shuffle";
import styles from "../../styles/Gallery.module.scss";
import { attrOutline, attribute } from "../../types/attributeType";
import {
  AnswerUpdateHistory,
  Nft,
  SecondNftForGallery,
} from "../../types/nftType";
import { ProductModalType } from "../../types/productModalType";
import { ViewType, isViewTypeForSecond } from "../../types/viewType";

import { MOBILE_MAX_WIDTH } from "../../constant";
// import { saleState } from "../../store/sale";

import FilterCondTagsArea from "../../components/gallery/FilterCondTagsArea";
import GeneDetail from "../../components/gallery/GeneDetail";
import GeneList from "../../components/gallery/GeneList";
import GeneListTabsArea from "../../components/gallery/GeneListTabsArea";
import ModalOverlay from "../../components/gallery/ModalOverlay";
import SearchInputBox from "../../components/gallery/SearchInputBox";
import SideNavItem from "../../components/gallery/SideNavItem";

import Modal from "../../components/common/Modal";
import SubPageLayout from "../../components/common/layouts/SubPageLayout";
import HelpBtn from "../../components/common/ui/HelpBtn/inex";
import NormalAnimatedBtn from "../../components/common/ui/NormalAnimatedBtn";
import SecondGeneDetail from "../../components/gallery/SecondGeneDetail";
import {
  Messages,
  injected,
  isNoMetamaskError,
  isUnsupportedChainError,
} from "../../libs/connectors";
import { isSp } from "../../libs/device";
import {
  getClientAnswerSignature,
  hasWindowEthereum,
  openMetamaskApp,
  updateAnswer,
  updateAnswerWithAttribute,
} from "../../libs/ethers";
import {
  Attribute,
  SecondAttribute,
  useAttributesLazyQuery,
  useGetParamsForUpdateAnswerWithAttributeLazyQuery,
  useNftsLazyQuery,
  useSecondAttributesLazyQuery,
  useSecondNftsLazyQuery,
  useUpdateAnswerMutation,
} from "../../libs/schema";
import {
  displayAttribute,
  displaySecondAttribute,
} from "../../services/attribute";
import { useTranslation } from "../../services/i18n";

type NftListObj = {
  NORMAL: Nft[];
  MYGENE: Nft[];
  EDITED: Nft[];
  SECOND_NORMAL: SecondNftForGallery[];
  SECOND_MYGENE: SecondNftForGallery[];
  SECOND_EDITED: SecondNftForGallery[];
};

const navItemKeyPrefixMap: Record<ViewType, string> = {
  NORMAL: "all-nav-",
  MYGENE: "my-nav-",
  EDITED: "edit-nav-",
  SECOND_NORMAL: "second-all-nav-",
  SECOND_MYGENE: "second-my-nav-",
  SECOND_EDITED: "second-edit-nav-",
};
const genViewTypeAndEmptyArrObj = (): Record<ViewType, any[]> => ({
  NORMAL: [],
  MYGENE: [],
  EDITED: [],
  SECOND_NORMAL: [],
  SECOND_MYGENE: [],
  SECOND_EDITED: [],
});

// サイドバーの属性配列作成
const createAttributes = (
  attrs: Attribute[] | SecondAttribute[],
  isForSecond: boolean
) => {
  let attrItems: Array<attrOutline> = [];
  let attrItemsOfficial: Array<attrOutline> = [];

  attrs.forEach((item, index) => {
    const targetAttr = attrItems.find((attr) => item.label == attr.label);
    if (!targetAttr) {
      // 対象のラベルがなければ新規作成
      let attrObj: attrOutline = {
        label: item.label,
        attributes: [
          {
            label: item.label,
            content: item.content,
            count: item.count,
            checked: false,
          },
        ],
      };
      attrItems.push(attrObj);
    } else {
      // 対象のラベルの属性配列に入れる
      attrItems.some((obj, attrIndex) => {
        if (obj.label === item.label) {
          attrItems[attrIndex].attributes.push({
            label: item.label,
            content: item.content,
            count: item.count,
            checked: false,
          });
          return true;
        }
      });
    }
  });

  // AtoZ順にcontentを並び替える
  attrItems.forEach((attr, index) => {
    attr.attributes.sort((a, b) => {
      if (a.content > b.content) {
        return 1;
      }
      if (a.content < b.content) {
        return -1;
      }
      return 0;
    });
  });

  // 表示順
  const keyArray = isForSecond
    ? [
        "attributeSpecial",
        "attributeHairFront",
        "attributeEar",
        "attributeNeck",
        "attributeCostume",
        "attributeFace",
        "attributeMouth",
        "attributeEye",
        "attributeSkin",
        "attributeHairBack",
        "attributeBackground",
        "geneType",
      ]
    : [
        "attributeSkinTone",
        "attributeHairFront",
        "attributeHairBack",
        "attributeEyes",
        "attributeEar",
        "attributeFace",
        "attributeMouth",
        "attributeNeck",
        "attributeClothing",
        "attributeSpecial",
      ];

  // 表示順に整形する
  keyArray.forEach((attr, index) => {
    const targetAttr = attrItems.find((item) => item.label == attr);
    if (targetAttr) {
      attrItemsOfficial.push(targetAttr);
    }
  });

  return attrItemsOfficial;
};

const isGeneForSecond = (
  n: Nft | SecondNftForGallery
): n is SecondNftForGallery => !("attributeDna" in n);

const Products: NextPage = () => {
  const { active, activate, library, account } = useWeb3React();

  // mq
  const isMobile = useMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`, false);

  // metamaskとコネクトする
  const handleConnectMetamask = async () => {
    if (isSp() && !hasWindowEthereum) {
      openMetamaskApp();
    } else {
      activate(injected, (e) => {
        if (isUnsupportedChainError(e.message)) {
          alert(Messages.UnsupportedChainError);
        }

        if (isNoMetamaskError(e.message)) {
          alert(Messages.NoMetamaskError);
        }
      });
    }

    getMyGene();
    closeModal();
  };

  const [provider, setProvider] = useState<
    ethers.providers.Web3Provider | undefined
  >(undefined);

  // sale情報
  // const sale = useRecoilValue(saleState);

  // 現在表示されているView
  const [viewType, setViewType] = useState<ViewType>("NORMAL");

  // Reveal前かどうか
  // const [isBeforeReveal, setIsBeforeReveal] = useState(true);

  const [attrListObj, setAttrListObj] = useState<
    Record<ViewType, attrOutline[]>
  >(genViewTypeAndEmptyArrObj());
  const [originalGeneListObj, setOriginalGeneListObj] = useState<NftListObj>(
    genViewTypeAndEmptyArrObj()
  );
  const [geneListObj, setGeneListObj] = useState<NftListObj>(
    genViewTypeAndEmptyArrObj()
  );

  // サイドナビ用attr取得
  const [
    fetchAttributes,
    {
      /* loading, error, data */
    },
  ] = useAttributesLazyQuery();

  const deepCopyAttrOutlineArray = (aoArr: attrOutline[]) =>
    aoArr.map((ao) => ({
      label: ao.label,
      attributes: ao.attributes.map((a) => ({ ...a })),
    }));

  const doFetchAttributes = async () => {
    const { data } = await fetchAttributes();
    const attrs = data?.attributes;
    if (!attrs) return;

    const attrItemsOfficial = createAttributes(attrs, false);

    setAttrListObj({
      ...attrListObj,
      NORMAL: attrItemsOfficial,
      MYGENE: deepCopyAttrOutlineArray(attrItemsOfficial),
      EDITED: deepCopyAttrOutlineArray(attrItemsOfficial),
    });
  };

  // サイドバーの属性のチェックボックスを変更
  const handleChangeAttribute = (label: string, attr: attribute) => {
    const attrItems = attrListObj[viewType];
    const targetAttr = attrItems.find((attr) => label == attr.label);
    if (!targetAttr) {
      return;
    }
    // チェックボックスだが、同一内属性は一つまでしかチェックをつけられないようにする
    attrItems.some((obj) => {
      if (obj.label === label) {
        obj.attributes.map((item) => {
          if (item.content == attr.content) {
            item.checked = !attr.checked;
          } else {
            item.checked = false;
          }
        });
      }
    });

    setAttrListObj({ ...attrListObj, [viewType]: attrItems });

    productFilter(originalGeneListObj[viewType]);
  };

  // サイドバーの属性変更でGENEをフィルタリング
  const productFilter = (originalGeneItems: (Nft | SecondNftForGallery)[]) => {
    let targetGeneList: (Nft | SecondNftForGallery)[] = [];
    const checkedAttrList: {
      [key: string]: string | boolean | null | undefined;
    } = {};

    const attrItems = attrListObj[viewType];

    // チェックがついている属性をぬきだす
    attrItems.forEach((attr, index) => {
      const checkedAttr = attr.attributes.filter((item) => item.checked);
      checkedAttr.forEach((item) => {
        checkedAttrList[item.label] = item.content;
      });
    });

    // なにもチェックがない場合は全件表示
    if (!Object.keys(checkedAttrList)?.length) {
      setGeneListObj({ ...geneListObj, [viewType]: originalGeneItems });
      return;
    }

    // 属性ではないkey
    const noCoverKeys = isViewTypeForSecond(viewType)
      ? [
          "id",
          "credit",
          "answer",
          "owner",
          "question",
          "questionCategory",
          "tokenId",
        ]
      : [
          "attributeDna",
          "id",
          "isAnswerLocked",
          "isAttributeSet",
          "credit",
          "answer",
          "owner",
          "question",
          "questionCategory",
          "tokenId",
        ];

    originalGeneItems.forEach((gene, index) => {
      // ブラケット記法のkeyの型をこれで指定しているため。
      // いい方法ないのだろうか。。。
      const geneAttrList: {
        [key: string]:
          | string
          | boolean
          | null
          | undefined
          | AnswerUpdateHistory[];
      } = {};
      const filteredAttrList: {
        [key: string]:
          | string
          | boolean
          | null
          | undefined
          | AnswerUpdateHistory[];
      } = {};

      for (const key in gene) {
        if (!gene[key] || noCoverKeys.includes(key)) {
          continue;
        }
        geneAttrList[key] = gene[key];
      }

      // チェックされたフィルターと一致するかを属性単位でチェック
      for (const geneAttrKey in geneAttrList) {
        for (const checkedAttrKey in checkedAttrList) {
          if (
            geneAttrKey === checkedAttrKey &&
            geneAttrList[geneAttrKey] === checkedAttrList[checkedAttrKey]
          ) {
            filteredAttrList[geneAttrKey] = geneAttrList[geneAttrKey];
            break;
          }
        }
      }

      // チェックされたフィルター数と抽出した属性数が一致する場合（積集合）
      const isTarget =
        Object.keys(filteredAttrList)?.length ===
        Object.keys(checkedAttrList)?.length;

      if (isTarget) {
        targetGeneList.push(gene);
      }
    });

    setGeneListObj({ ...geneListObj, [viewType]: targetGeneList });
  };

  // 回答書き込み後
  const [isUpdateGene, setIsUpdateGene] = useState(false);

  const [
    fetchNfts,
    {
      /* loading, error, data */
    },
  ] = useNftsLazyQuery();

  const doFetchNfts = async () => {
    const { data } = await fetchNfts({
      variables: {
        nftFilterInput: {},
      },
    });
    const res = data?.nfts;

    const nftItems: Nft[] = [];

    res?.edges?.forEach((item, index) => {
      const node = item.node;

      const nft: Nft = {
        attributeClothing: node.attributeClothing,
        attributeDna: node.attributeDna,
        attributeEyes: node.attributeEyes,
        attributeEar: node.attributeEar,
        attributeFace: node.attributeFace,
        attributeHairBack: node.attributeHairBack,
        attributeHairFront: node.attributeHairFront,
        attributeMouth: node.attributeMouth,
        attributeNeck: node.attributeNeck,
        attributeSkinTone: node.attributeSkinTone,
        attributeSpecial: node.attributeSpecial,
        id: node.id,
        isAnswerLocked: node.isAnswerLocked,
        isAttributeSet: node.isAttributeSet,
        credit: node.credit ? node.credit : null,
        answer: node.answer ? node.answer : null,
        owner: node.owner,
        question: node.question,
        questionCategory: node.questionCategory ? node.questionCategory : null,
        tokenId: node.tokenId ? node.tokenId : null,
        imageId: node.imageId,
        answerUpdateHistories: node.answerUpdateHistories,
      };

      nftItems.push(nft);
    });

    const editedNftItems = nftItems.filter((item) => item.answer);

    setOriginalGeneListObj({
      ...originalGeneListObj,
      NORMAL: nftItems,
      EDITED: editedNftItems,
    });
    setGeneListObj({
      ...geneListObj,
      NORMAL: nftItems,
      EDITED: editedNftItems,
    });
  };

  const [fetchSecondAttributes] = useSecondAttributesLazyQuery();

  const doFetchSecondAttributes = async () => {
    const { data } = await fetchSecondAttributes();
    const attrs = data?.secondAttributes;
    if (!attrs) return;

    const attrItemsOfficial = createAttributes(attrs, true);

    setAttrListObj({
      ...attrListObj,
      SECOND_NORMAL: attrItemsOfficial,
      SECOND_MYGENE: deepCopyAttrOutlineArray(attrItemsOfficial),
      SECOND_EDITED: deepCopyAttrOutlineArray(attrItemsOfficial),
    });
  };

  const [fetchSecondNfts] = useSecondNftsLazyQuery();

  const doFetchSecondNfts = async () => {
    const { data } = await fetchSecondNfts({
      variables: { secondNftFilterInput: {} },
    });
    const nftItems: SecondNftForGallery[] = [];

    data?.secondNfts?.edges?.forEach((item, index) => {
      const node = item.node;
      nftItems.push({
        attributeBackground: node.attributeBackground,
        attributeCostume: node.attributeCostume,
        attributeEar: node.attributeEar,
        attributeEye: node.attributeEye,
        attributeFace: node.attributeFace,
        attributeHairBack: node.attributeHairBack,
        attributeHairFront: node.attributeHairFront,
        attributeMouth: node.attributeMouth,
        attributeNeck: node.attributeNeck,
        attributeSkin: node.attributeSkin,
        attributeSpecial: node.attributeSpecial,
        geneType: node.geneType,
        id: node.id,
        answer: node.answer,
        credit: node.credit,
        owner: node.owner,
        question: node.question,
        questionCategory: node.questionCategory,
        tokenId: node.tokenId,
        imageId: node.imageId,
        secondAnswerUpdateHistories: node.secondAnswerUpdateHistories,
      });
    });

    const editedNftItems = nftItems.filter((item) => item.answer);

    setOriginalGeneListObj({
      ...originalGeneListObj,
      SECOND_NORMAL: nftItems,
      SECOND_EDITED: editedNftItems,
    });
    setGeneListObj({
      ...originalGeneListObj,
      SECOND_NORMAL: nftItems,
      SECOND_EDITED: editedNftItems,
    });
  };

  // 回答とattribute書き込み時のパラメータを取得
  const [
    fetchParamsForUpdateAnswerWithAttribute,
    {
      /* loading, error, data */
    },
  ] = useGetParamsForUpdateAnswerWithAttributeLazyQuery();

  // 回答とattributeを書き込む(doUpdateAnswer内から呼ぶ)
  const doUpdateAnswerWithAttribute = async (
    tokenId: string,
    answer: string,
    credit: string
  ) => {
    const { data } = await fetchParamsForUpdateAnswerWithAttribute({
      variables: {
        tokenId: Number(tokenId),
      },
    });

    if (!data) {
      throw new Error("cannot update answer");
    }

    await updateAnswerWithAttribute(
      library,
      Number(tokenId),
      answer,
      credit,
      data.getParamsForUpdateAnswerWithAttribute
    );
  };

  // 回答を書き込む
  const doUpdateAnswer = async (
    tokenId: string,
    answer: string,
    credit: string
  ) => {
    setIsUpdateGene(false);

    const { data } = await fetchNfts({
      variables: {
        nftFilterInput: {
          tokenId: Number(tokenId),
        },
      },
    });
    if (!data || !data.nfts.edges || data.nfts.edges.length < 1) {
      throw new Error("cannot update answer");
    }

    const { isAnswerLocked, isAttributeSet } = data.nfts.edges[0].node;
    if (isAnswerLocked) {
      throw new Error("cannot update answer: answer is locked");
    }

    if (isAttributeSet) {
      // 2回目以降の書き込みの場合
      await updateAnswer(library, tokenId, answer, credit);
    } else {
      // 1回目の場合はattributeと一緒に書き込む
      await doUpdateAnswerWithAttribute(tokenId, answer, credit);
    }

    // 回答書き込みの通知
    setIsUpdateGene(true);
  };

  const [updateSecondNftAnswerMutation] = useUpdateAnswerMutation();

  const updateSecondNftAnswer = async (
    tokenId: string,
    answer: string,
    credit: string
  ) => {
    setIsUpdateGene(false);

    const clientSig = await getClientAnswerSignature(library);
    const res = await updateSecondNftAnswerMutation({
      variables: { clientSig, answer, credit, tokenId: Number(tokenId) },
    });

    if (res.data?.updateAnswer) setIsUpdateGene(true);
  };

  // シリアルナンバー検索
  const [sortSerialText, setSortSerialText] = useState("");
  const onChangeSortSerialText = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setSortSerialText(event.target.value);
    searchSerial(event.target.value);
  };

  const searchSerial = (value: string) => {
    if (value === "") {
      // チェックがついている属性をぬきだす
      let checkedAttrList: Array<string | null | undefined> = [];
      attrListObj[viewType].forEach((attr, index) => {
        const checkedAttr = attr.attributes.filter((item) => item.checked);
        checkedAttr.forEach((item) => {
          checkedAttrList.push(item.content);
        });
      });

      // GENEのフィルタリング
      if (checkedAttrList.length > 0) {
        productFilter(originalGeneListObj[viewType]);
      } else {
        setGeneListObj({
          ...geneListObj,
          [viewType]: originalGeneListObj[viewType],
        });
      }

      return;
    }

    const geneArray = originalGeneListObj[viewType] as (
      | Nft
      | SecondNftForGallery
    )[];
    const filteredList = geneArray.filter((gene: Nft | SecondNftForGallery) => {
      if (gene.tokenId) {
        return Number(value.trim()) == Number(gene.tokenId);
      }
    });
    setGeneListObj({ ...geneListObj, [viewType]: filteredList });
    return;
  };

  // モード: my_gene かどうか
  const isMyGeneView = (viewType: ViewType) => {
    return viewType === "MYGENE" || viewType === "SECOND_MYGENE";
  };

  // モード: edit かどうか
  const isEditView = (viewType: ViewType) => {
    return viewType == "EDITED";
  };

  // my gene
  const getMyGene = () => {
    setViewType(isViewTypeForSecond(viewType) ? "SECOND_MYGENE" : "MYGENE");
  };

  const onClickMyCollectionBtn = () => {
    if (active) {
      getMyGene();
    } else {
      setModalType("WALLET");
      setIsShowModal(true);
    }
  };

  // edited
  const getEdited = () => {
    setViewType(isViewTypeForSecond(viewType) ? "SECOND_EDITED" : "EDITED");
  };

  // シャッフル
  const getShuffleGene = () => {
    const productItems = originalGeneListObj[viewType];
    const shuffledArray = shuffleArray(productItems);

    // リスト全件を更新する
    setOriginalGeneListObj({
      ...originalGeneListObj,
      [viewType]: shuffledArray,
    });

    // フィルターを元に表示GENEを更新する
    productFilter(shuffledArray);
  };

  const getAllGene = () => {
    setViewType("NORMAL");
  };

  const checkedAttrList = useMemo(() => {
    const targetAttrList = attrListObj[viewType];

    const aoList: Array<attrOutline> = [];
    for (const ao of targetAttrList) {
      const attributes = ao.attributes.filter((attr) => attr.checked);
      if (attributes.length === 0) continue;

      aoList.push({
        label: ao.label,
        attributes,
      });
    }

    return aoList;
  }, [viewType, attrListObj]);

  // モーダル表示
  const [isShowModal, setIsShowModal] = useState(false);

  const [modalType, setModalType] = useState<ProductModalType | null>(null);

  const closeModal = () => {
    setIsUpdateGene(false);
    setIsShowModal(false);
  };

  // 選択した 1st の GENE
  const [selectedGene, setSelectedGene] = useState<Nft | null>(null);
  // 選択した 1st の GENE_INDEX
  const [selectedGeneIndex, setSelectedGeneIndex] = useState<number>(0);
  // 選択した 2nd の GENE
  const [selectedSecondGene, setSelectedSecondGene] =
    useState<SecondNftForGallery | null>(null);
  // 選択した 1st の GENE_INDEX
  const [selectedSecondGeneIndex, setSelectedSecondGeneIndex] =
    useState<number>(0);

  const showGeneDetailModal = (
    gene: Nft | SecondNftForGallery,
    geneIndex: number
  ) => {
    if (isGeneForSecond(gene)) {
      setSelectedSecondGene(gene);
      setSelectedSecondGeneIndex(geneIndex);
    } else {
      setSelectedGene(gene);
      setSelectedGeneIndex(geneIndex);
    }

    setModalType("DETAIL");
    setIsShowModal(true);
  };

  // SP: フィルターモーダル表示
  const [filterOpen, isFilterOpen] = useState(false);

  const toggleFilterBtn = () => {
    isFilterOpen(!filterOpen);
  };

  const [fetched1stData, updateFetched1stData] = useReducer(() => true, false);
  const fetch1stData = async () => {
    await doFetchAttributes();
    await doFetchNfts();
    updateFetched1stData();
  };

  const [fetched2ndData, updateFetched2ndData] = useReducer(() => true, false);
  const fetch2ndData = async () => {
    await doFetchSecondAttributes();
    await doFetchSecondNfts();
    updateFetched2ndData();
  };

  const fmtAttribute = isViewTypeForSecond(viewType)
    ? displaySecondAttribute
    : displayAttribute;

  useEffect(() => {
    if (isViewTypeForSecond(viewType) && !fetched2ndData) fetch2ndData();
    if (!isViewTypeForSecond(viewType) && !fetched1stData) fetch1stData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType]);

  // リビール前かどうかを設定
  // useEffect(() => {
  //   // 現在時間
  //   const now = new Date();

  //   const revealStartTime = new Date(sale.revealStartTime);
  //   const beforeRevealTime = now < revealStartTime;
  //   setIsBeforeReveal(beforeRevealTime);
  // }, [sale]);

  useEffect(() => {
    if (library === undefined) return;
    setProvider(new ethers.providers.Web3Provider(library.provider));
  }, [library]);

  // connect walletした時に走る処理
  useEffect(() => {
    if (!isMyGeneView(viewType)) return;
    if (provider === undefined) return;
    if (account === undefined || account === null) return;

    // connect wallet したアカウントのアドレス
    console.info({ account });

    const geneArray = originalGeneListObj[
      viewType === "SECOND_MYGENE" ? "SECOND_NORMAL" : "NORMAL"
    ] as (Nft | SecondNftForGallery)[];
    const allMyGenes = geneArray.filter((g) => g.owner === account);
    setOriginalGeneListObj({
      ...originalGeneListObj,
      [viewType]: allMyGenes,
    });
    setGeneListObj({
      ...geneListObj,
      [viewType]: allMyGenes,
    });

    // フィルターを元に表示GENEを更新する
    productFilter(allMyGenes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, account, library, viewType]);

  return (
    <>
      <SubPageLayout
        title="GALLERY"
        titleImgSrc="/images/text_images/gallery_vertical.svg"
        bgImgUrl="/images/bg-gallery-pc.jpg"
        spBgImgUrl="/images/bg-gallery-sp.jpg"
        isMobile={isMobile}
        extraContainerClass={
          isMobile
            ? "min-h-[calc(100vh-40px)]"
            : "min-h-[calc(100vh-116px)] mt-[36px]"
        }
        isForGallery={true}
      >
        {/* GENE表示エリア */}
        <div className={styles["gallery-main-area"]}>
          {!isMobile ? (
            // PCサイドバー
            <div className={styles["gallery-main-side"]}>
              <div className={styles["side-nav-title"]}>FILTER</div>
              <div className={`${styles["side-nav-main"]}`}>
                <FilterContent
                  navSortText={sortSerialText}
                  onChangeInput={onChangeSortSerialText}
                  navItems={attrListObj[viewType]}
                  navItemKeyPrefix={navItemKeyPrefixMap[viewType]}
                  handleChangeAttribute={handleChangeAttribute}
                  navClassName={`${styles.sideFilterNav} ${
                    sortSerialText ? styles.navDisable : ""
                  }`}
                  onClickMyCollectionBtn={onClickMyCollectionBtn}
                  onClickEditBtn={getEdited}
                  fmtAttribute={fmtAttribute}
                />
              </div>
            </div>
          ) : (
            // SPフィルター
            <div
              className={`${styles["filter-modal"]} ${
                filterOpen && styles["is-show"]
              }`}
            >
              <div className={styles["filter-modal-header"]}>
                <p className={styles["filter-modal-title"]}>FILTER</p>
                <button
                  type="button"
                  className={styles["close-button"]}
                  onClick={toggleFilterBtn}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text */}
                  <img src="/images/close.png" width="20" />
                </button>
              </div>
              <div className={styles["filter-modal-content"]}>
                <FilterContent
                  navSortText={sortSerialText}
                  onChangeInput={onChangeSortSerialText}
                  navItems={attrListObj[viewType]}
                  navItemKeyPrefix={navItemKeyPrefixMap[viewType]}
                  handleChangeAttribute={handleChangeAttribute}
                  navClassName={`${styles["filter-nav"]} ${
                    sortSerialText ? styles["nav-disable"] : ""
                  }`}
                  onClickMyCollectionBtn={onClickMyCollectionBtn}
                  onClickEditBtn={getEdited}
                  fmtAttribute={fmtAttribute}
                />
              </div>
            </div>
          )}

          <div className={styles["gallery-main-header-and-genes"]}>
            <div className="">
              <GeneListTabsArea
                viewType={viewType}
                setViewType={setViewType}
                count={geneListObj[viewType]?.length ?? 0}
                onClickShuffle={getShuffleGene}
                onClickToggleFilterBtn={toggleFilterBtn}
                isFilterBtnDisable={isMyGeneView(viewType) && !active}
              />
              <FilterCondTagsArea
                attrOutlineList={checkedAttrList}
                handleChangeAttribute={handleChangeAttribute}
                viewType={viewType}
                setViewType={setViewType}
              />
            </div>

            <div className={styles["gallery-main-genes"]}>
              <GeneList
                geneList={geneListObj[viewType]}
                // isBeforeReveal={isBeforeReveal}
                isBeforeReveal={false}
                onClickGene={showGeneDetailModal}
                isForSecond={isViewTypeForSecond(viewType)}
              />
            </div>
          </div>
        </div>
      </SubPageLayout>

      {/* GENE詳細モーダル */}
      <ModalOverlay showModal={isShowModal} closeModal={closeModal}>
        {(() => {
          if (modalType == "DETAIL") {
            return isViewTypeForSecond(viewType) ? (
              <SecondGeneDetail
                selectedGene={selectedSecondGene!}
                selectedGeneIndex={selectedSecondGeneIndex}
                geneList={geneListObj[viewType]}
                isMyGene={isMyGeneView(viewType)}
                isUpdateGene={isUpdateGene}
                // isBeforeReveal={isBeforeReveal}
                isBeforeReveal={false}
                onClickCloseBtn={closeModal}
                handleEdit={updateSecondNftAnswer}
              />
            ) : (
              <GeneDetail
                selectedGene={selectedGene!}
                selectedGeneIndex={selectedGeneIndex}
                geneList={geneListObj[viewType]}
                isMyGene={isMyGeneView(viewType)}
                isUpdateGene={isUpdateGene}
                // isBeforeReveal={isBeforeReveal}
                isBeforeReveal={false}
                onClickCloseBtn={closeModal}
                handleEdit={doUpdateAnswer}
              />
            );
          } else if (modalType == "WALLET") {
            return (
              <Modal
                onClickBtn={handleConnectMetamask}
                closeModal={closeModal}
                title="CONNECT WALLET"
                btnText="META MASK"
              ></Modal>
            );
          }
        })()}
      </ModalOverlay>
    </>
  );
};

const FilterContent = ({
  navSortText,
  onChangeInput,
  navItems,
  navItemKeyPrefix,
  handleChangeAttribute,
  navClassName,
  onClickMyCollectionBtn,
  onClickEditBtn,
  fmtAttribute,
}: {
  navSortText: string;
  onChangeInput: (event: React.ChangeEvent<HTMLInputElement>) => void;
  navItems: Array<attrOutline>;
  navItemKeyPrefix: string;
  handleChangeAttribute: (label: string, attr: attribute) => void;
  navClassName: string;
  onClickMyCollectionBtn: () => void;
  onClickEditBtn: () => void;
  fmtAttribute: (attribute: string) => string;
}) => {
  const t = useTranslation();

  const isMobile = useMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`, false);

  return (
    <>
      <div className={isMobile ? "px-[16px] mb-[16px]" : ""}>
        <SearchInputBox
          value={navSortText}
          onChange={onChangeInput}
          placeholder="sort by serial"
        />
        <div
          className={`grid ${
            isMobile
              ? "grid-columns-3 grid-cols-[148px_148px_1fr]"
              : "grid-rows-2 grid-cols-[202px_1fr] gap-[8px]"
          }`}
        >
          <div className="row-start-1">
            <NormalAnimatedBtn
              onClick={onClickMyCollectionBtn}
              extraClass="w-full"
            >
              MY COLLECTION
            </NormalAnimatedBtn>
          </div>
          <div className={isMobile ? "ml-[15px]" : "row-start-2"}>
            <NormalAnimatedBtn onClick={onClickEditBtn} extraClass="w-full">
              EDITED
            </NormalAnimatedBtn>
          </div>
          <HelpBtn
            href={t("howToUrl") as string}
            extraClass={`self-center justify-self-end ${
              isMobile ? "" : "row-start-2 col-start-2 "
            }`}
          />
        </div>
      </div>
      <nav className={navClassName}>
        {navItems.map((attr, index) => (
          <SideNavItem
            key={`${navItemKeyPrefix}${index}`}
            label={attr.label}
            attributes={attr.attributes}
            onChangeCheckbox={handleChangeAttribute}
            fmtAttribute={fmtAttribute}
          />
        ))}
      </nav>
    </>
  );
};

export default Products;
